import { autenticacionService } from '@/app/autenticacion/api/autenticacionService';
import { MenuType } from '@/app/autenticacion/types/MenuType';
import { cookieStorageService } from '@/shared/services/cookieStorageService';
import { localStorageService } from '@/shared/services/localStorageService';

const MENU_CACHE_KEY = 'menu';
const MENU_CACHE_TS_KEY = 'menu_ts';
const MENU_TTL_MS = 30 * 60 * 1000; // 30 minutos

// Dedup: si ya hay una petición en vuelo, reutilizarla
let inflightRequest: Promise<MenuType[]> | null = null;

/**
 * Única frontera de lectura del menú desde cache (localStorage) o API.
 * Normaliza el payload crudo a MenuType[] tipado.
 * Todos los hooks de menú deben pasar por aquí.
 * Cache TTL: 30 minutos. Dedup de peticiones concurrentes.
 */
export async function getMenuFromCache(): Promise<MenuType[]> {
  const raw = localStorageService?.getItem(MENU_CACHE_KEY);
  const cachedTs = localStorageService?.getItem(MENU_CACHE_TS_KEY);

  if (raw != null && cachedTs != null) {
    const elapsed = Date.now() - Number(cachedTs);
    if (elapsed < MENU_TTL_MS) {
      const parsed: MenuType[] = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return parsed ?? [];
    }
  }

  const token = cookieStorageService?.getItem('accessToken');
  if (!token) return [];

  // Si ya hay una petición en vuelo, esperar su resultado
  if (inflightRequest) return inflightRequest;

  inflightRequest = autenticacionService
    .obtenerMenu()
    .then((response) => {
      const menuList: MenuType[] = response.data.dataResponse ?? [];
      localStorageService.setItem(MENU_CACHE_KEY, JSON.stringify(menuList));
      localStorageService.setItem(MENU_CACHE_TS_KEY, String(Date.now()));
      return menuList;
    })
    .finally(() => {
      inflightRequest = null;
    });

  return inflightRequest;
}

/**
 * Lectura síncrona del menú desde localStorage.
 * Para guards que no pueden ser async.
 */
export function getMenuFromCacheSync(): MenuType[] {
  const raw = localStorageService?.getItem(MENU_CACHE_KEY);
  if (raw == null) return [];
  const parsed: MenuType[] = typeof raw === 'string' ? JSON.parse(raw) : raw;
  return parsed ?? [];
}
